import { Autocomplete, TextField, TextFieldProps } from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import { RowFieldBase } from '../row-field-base';

export type Value = string | number | null;
type Option = { id: Value; name: string };

type AutocompleteProps = React.ComponentProps<typeof Autocomplete<Option>>;
type RenderInput = AutocompleteProps['renderInput'];

type Props = Omit<AutocompleteProps, 'renderInput' | 'onChange' | 'value' | 'defaultValue'> &
  Pick<TextFieldProps, 'label' | 'name' | 'error' | 'helperText' | 'margin'> & {
    onChange?: (value: Value) => void;
    readOnly?: boolean;
    isRow?: boolean;
    ariaLabel?: string;
    value?: Value;
    defaultValue?: Value;
  };

const AutocompleteField: React.FC<Props> = ({
  label,
  error,
  helperText,
  margin,
  readOnly,
  isRow,
  id,
  disabled,
  value,
  defaultValue,
  options,
  onChange,
  size,
  ...autoCompleteProps
}: Props) => {
  const Input = useMemo<RenderInput>(
    () =>
      function Input(autocompleteInputProps) {
        const { disabled: _inputDisabled, InputProps, ...otherInputProps } = autocompleteInputProps;
        const inputProps = readOnly === undefined ? InputProps : { ...InputProps, readOnly };

        return (
          <TextField
            disabled={disabled}
            label={isRow ? undefined : label}
            error={error}
            helperText={helperText}
            InputProps={inputProps}
            margin={margin ?? (isRow ? 'none' : undefined)}
            {...otherInputProps}
          />
        );
      },
    [disabled, error, helperText, isRow, label, margin, readOnly],
  );

  const getOptionLabel: NonNullable<AutocompleteProps['getOptionLabel']> = useCallback(
    (option): string => option.name,
    [],
  );
  const handleChange: NonNullable<AutocompleteProps['onChange']> = useCallback(
    (event, value) => {
      onChange?.(value === null ? null : value.id);
    },
    [onChange],
  );

  const valueOption = value !== undefined ? (options.find(({ id }) => id === value) ?? null) : undefined;
  const defaultValueOption =
    defaultValue !== undefined ? (options.find(({ id }) => id === defaultValue) ?? null) : undefined;

  const autocompleteDisabled = disabled || readOnly;
  const defaultedSize = (size ?? isRow) ? 'small' : undefined;
  const autoComplete = (
    <Autocomplete
      id={id}
      disabled={autocompleteDisabled}
      readOnly={readOnly}
      renderInput={Input}
      defaultValue={defaultValueOption}
      value={valueOption}
      onChange={handleChange}
      options={options}
      getOptionLabel={getOptionLabel}
      size={defaultedSize}
      {...autoCompleteProps}
    />
  );

  if (isRow) {
    return (
      <RowFieldBase id={id} label={label}>
        {autoComplete}
      </RowFieldBase>
    );
  }

  return autoComplete;
};

export { AutocompleteField };
